import { supabase } from '@/lib/supabase'
import { queryClient } from '@/lib/query-client'
import type { CategoryRow } from '@/lib/db-types'

export type CategoryRef = Pick<CategoryRow, 'id' | 'name' | 'parent_id' | 'color'>

export type TagRef = { id: string; name: string; color: string }

export type ProfileInfo = { display_name: string; avatar_url: string | null }

export function buildProfileMap(profiles: { id: string; display_name: string; avatar_url: string | null }[] | null) {
  return new Map(profiles?.map(p => [p.id, { display_name: p.display_name, avatar_url: p.avatar_url } as ProfileInfo]) ?? [])
}

export function buildCategoryMap(categories: CategoryRef[] | null) {
  return new Map(categories?.map(c => [c.id, c]) ?? [])
}

export function buildTagMap(tags: TagRef[] | null) {
  return new Map(tags?.map(t => [t.id, t]) ?? [])
}

export async function fetchProfilesData() {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, display_name, avatar_url')
    .order('created_at')
  if (error) throw error
  return data || []
}

export async function fetchCategoriesData() {
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .order('sort_order', { ascending: true })
  if (error) throw error
  return data || []
}

export async function fetchTagsData(): Promise<TagRef[]> {
  const { data, error } = await supabase
    .from('tags')
    .select('id, name, color')
    .order('name')
  if (error) throw error
  return (data ?? []) as TagRef[]
}

const STALE_TIME = 5 * 60 * 1000

export async function fetchMaps() {
  const [profiles, categories, tags] = await Promise.all([
    queryClient.fetchQuery({
      queryKey: ['profiles'],
      queryFn: fetchProfilesData,
      staleTime: STALE_TIME,
    }),
    queryClient.fetchQuery({
      queryKey: ['categories'],
      queryFn: fetchCategoriesData,
      staleTime: STALE_TIME,
    }),
    queryClient.fetchQuery({
      queryKey: ['tags'],
      queryFn: fetchTagsData,
      staleTime: STALE_TIME,
    }),
  ])
  return {
    profileMap: buildProfileMap(profiles),
    categoryMap: buildCategoryMap(categories),
    tagMap: buildTagMap(tags),
  }
}

export function enrichItems(
  items: Array<{ id: string; name: string; amount: number; category_id: string | null; transaction_id?: string }>,
  categoryMap: Map<string, CategoryRef>,
) {
  return items.map(item => {
    const cat = item.category_id ? categoryMap.get(item.category_id) : null
    const parent = cat?.parent_id ? categoryMap.get(cat.parent_id) : null
    return {
      id: item.id,
      name: item.name,
      amount: item.amount,
      category_id: item.category_id,
      category_name: cat?.name ?? null,
      parent_category_name: parent?.name ?? null,
      parent_category_color: parent?.color ?? cat?.color ?? null,
    }
  })
}
